import React, {Fragment} from 'react';
import classnames from 'classnames';
import lodash from 'lodash';
import styles from './../StudyDocuments.scss';
import LifecycleIndicator from 'components/common/Study/LifecycleIndicator';
import {Link} from 'react-router';
import {FILED, UNFILED} from 'common/util/filingHelpers';
import {
    UserCanUploadFiles,
    UserCanViewDocumentDetails,
    UserCanManageStudyExpectations
} from 'common/permissions/components/actionButton';
import { CustomToolTip } from 'components/common/CustomTooltip/CustomTooltip';
import {Conditional} from 'common/components/layout';

export const UploadedDocumentsDetails = ({uploadedDocuments, onError, onFile, expectation}) => {


    return lodash.map(uploadedDocuments, (document, index) => {

        const hasDuplicates = document.duplicates.length > 0;

        return (
            <div
                key={index}
                className={styles.documentDetails}
                data-cy="shareComponent"
            >
                <h2
                    className={classnames(
                        styles.detailsLabel,
                        {
                            [styles.error]: hasDuplicates
                        }
                    )}
                    onClick={() =>
                        hasDuplicates
                            ? onError(document, UNFILED, expectation.sourceId)
                            : onFile(document.id, UNFILED, expectation.sourceId)
                    }
                    data-cy="shareComponent-h2"
                >
                    <ErrorIndicator hasDuplicates={hasDuplicates}/>
                    {document.name}
                </h2>
                <p
                    className={styles.detailsButton}
                    onClick={() =>
                        onFile(document.id, UNFILED, expectation.sourceId)
                    }
                    data-cy="shareComponent-p"
                >
                    {!hasDuplicates && 'File Document'}
                </p>
            </div>
        );
    });
};

export const ErrorIndicator = ({hasDuplicates}) => {
    return (
        hasDuplicates &&
        <Fragment data-cy="shareComponent-ErrorIndicator">
            <i className={styles.icon}>{'info'}</i>Duplicate -
        </Fragment>
    );
};


export const CardLabels = ({cardTitle, cardStatus}) => {
    return (
        <div data-cy="shareComponent-CardLabels">
            <h2
                className={styles.expectationCardTitle}
                data-cy="shareComponent-h2"
            >
                {cardTitle}
            </h2>
            <LifecycleIndicator
                className={classnames(
                    styles.expectationStatus,
                    {
                        [styles.expectationStatusRedBackground]: cardStatus === 'MISSING'
                    },
                )}
                withBackground={cardStatus === 'FILED'}
                title={cardStatus}
            />
        </div>
    );
};

export const CardControls = ({
    expectation,
    openFilePicker,
    setUploadedFileToExpectation,
    unfolded,
    onClick
}) => {
    const UploadIcon = () => (
        <i
            className={styles.upload}
            onClick={() => {
                setUploadedFileToExpectation(expectation);
                openFilePicker(expectation);
            }}
        >
            {'cloud_upload'}
        </i>
    );
    const SecuredUploadIcon = UserCanUploadFiles(UploadIcon);
    return (
        <div
            className={styles.cardControls}
            data-cy="shareComponent-CardControls"
        >
            <SecuredUploadIcon/>
            <p
                className={styles.foldedLabel}
                onClick={onClick}
                data-cy="shareComponent-onClick"
            >
                {unfolded ? 'Collapse' : 'Expand'}
            </p>
            <i
                className={styles.unfold}
                onClick={onClick}
                data-cy="shareComponent-i"
            >
                {unfolded ? 'unfold_less' : 'unfold_more'}
            </i>
        </div>
    );
};

export const CardExpectationDocuments = (props) => {
    const {
        expectation,
        binderId,
        onError,
        onFile,
        filteredUploadedDocuments,
        filteredDocuments,
        hasDocument,
        studyId
    } = props;
    if (hasDocument) {
        return  (
            <Fragment data-cy="shareComponent-CardExpectationDocuments">
                <UploadedDocumentsDetails
                    uploadedDocuments={filteredUploadedDocuments}
                    expectation={expectation}
                    onError={onError}
                    onFile={onFile}
                />
                <DocumentDetails
                    filedDocuments={filteredDocuments}
                    binderId={binderId}
                    studyId={studyId}
                    expectation={expectation}
                    onFile={onFile}
                />
            </Fragment>);
    } else {
        return (<NoDocument/>);
    }
};


export const DocumentDetails = ({ filedDocuments = [], binderId, expectation, onFile, studyId }) => {
    return filedDocuments.map((document, index) => {
        return (
            <DocumentCard
                key={index}
                binderId={binderId}
                document={document}
                index={index}
                studyId={studyId}
                filingType={FILED}
                sourceId={expectation.sourceId}
                onFile={onFile}
            />
        );
    });
};

export const NoDocument = () => {
    return (
        <div
            className={styles.documentDetails}
            data-cy="shareComponent-NoDocument"
        >
            <h2
                className={styles.noDocumentDetailsLabel}
            >
                No documents have been uploaded and filed.
            </h2>
        </div>
    );
};


export const DocumentCard = ({document, binderId, index, filingType, sourceId, onFile, studyId}) => {

    let path = `/binder/${binderId}/files/${document.id}`;

    if (studyId) {
        path = `/study/${studyId}/files/${document.id}?from=expectations`;
    }

    const RefileDocumentLink = () =>
        <p
            className={styles.detailsButton}
            onClick={() => onFile(document.id, filingType, sourceId)}
        >
            {document.documentType ? 'Refile Document' : null}
        </p>;

    const ClickableDocumentName = () =>
        <Link
            className={styles.detailsLabel}
            to={path}
            target={studyId ? '' : '_blank'}
        >
            {document.name}
        </Link>;


    const DocumentName = () =>
        <p
            className={classnames(styles.detailsLabel, styles.defaultCursor)}
        >
            {document.name}
        </p>;

    const SecuredRefileDocumentLink = UserCanManageStudyExpectations(RefileDocumentLink);
    const SecuredDocumentNameLink = UserCanViewDocumentDetails(ClickableDocumentName, DocumentName);

    return (
        <div
            key={index}
            className={styles.documentDetails}
        >
            <SecuredDocumentNameLink/>
            <SecuredRefileDocumentLink/>
        </div>
    );
};

export const DisabledCardTooltip = ({hasDocumentAccess}) => {
    const permissionsToolTipInfo = 'You do not have permissions to access this document.';
    return (
        <Conditional condition={!hasDocumentAccess}>
            <CustomToolTip title={permissionsToolTipInfo} placement="right" interactive>
                <i className={styles.tooltipIcon}/>
            </CustomToolTip>
        </Conditional>
    );
};
